/**
 * The signature of the app: months as a ledger, each with a seal.
 *   brass disc + mark  published (sealed, immutable)
 *   dashed apricot     in review (statements processed, not yet sealed)
 *   dashed outline     the open plan (current month)
 *   faint ring         a past month waiting to be closed
 * When a month is published, its seal is stamped once on Home.
 */
import { useEffect, useRef, useState } from 'react';
import type { Month } from '../data/types';
import { monthKey } from '../lib/periods';

export type SealState = 'published' | 'review' | 'open' | 'draft';

export function sealState(m: Month, openStart: string | undefined): SealState {
  if (m.status === 'published') return 'published';
  if (m.month_start === openStart) return 'open';
  if (m.status === 'review') return 'review';
  return 'draft';
}

const SEAL_WORD: Record<SealState, string> = { published: 'sealed', review: 'in review', open: 'open plan', draft: 'not closed' };

export function Seal({ state, stamp = false }: { state: SealState; stamp?: boolean }) {
  return (
    <svg className={`seal seal-${state} ${stamp ? 'seal-stamp' : ''}`} viewBox="0 0 30 30" aria-hidden="true">
      <circle className="ring" cx="15" cy="15" r="14" />
      <circle className="disc" cx="15" cy="15" r="10.5" />
      {state === 'published' && <path className="mark" d="M10.5 15.2l3 3 6-6.4" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

/** Horizontal ledger of months, oldest to newest, scrolled to the newest. */
export function LedgerStrip({ months, openStart, selected, onSelect, stamp, label = 'Months' }: {
  months: Month[]; openStart: string | undefined; selected?: string; onSelect: (key: string) => void; stamp?: string | null; label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const ordered = [...months].sort((a, b) => a.month_start.localeCompare(b.month_start)).slice(-12);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const current = el.querySelector<HTMLElement>('[aria-current="true"]');
    el.scrollLeft = current ? Math.max(0, current.offsetLeft + current.offsetWidth - el.clientWidth + 12) : el.scrollWidth;
    edge();
  }, [ordered.length, selected]);
  // Fade the left edge only when earlier months are scrolled out of view, so nothing looks cut off by accident.
  const edge = () => ref.current?.classList.toggle('scrolled-left', ref.current.scrollLeft > 4);
  return (
    <div className="ledger-strip" ref={ref} onScroll={edge}><ul className="ledger-strip-list" aria-label={label}>
      {ordered.map((m) => {
        const key = monthKey(m.month_start);
        const state = sealState(m, openStart);
        const d = new Date(`${m.month_start}T00:00:00`);
        const name = d.toLocaleDateString('en-GB', { month: 'short' });
        return (
          <li key={m.id}><button type="button" className="lm" aria-current={selected === key ? 'true' : undefined}
            aria-label={`${d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}, ${SEAL_WORD[state]}`}
            onClick={() => onSelect(key)}>
            <Seal state={state} stamp={stamp === key} />
            <span className="lm-name">{name}</span>
            {d.getMonth() === 0 || m === ordered[0] ? <span className="lm-year">{d.getFullYear()}</span> : null}
          </button></li>
        );
      })}
    </ul></div>
  );
}

/* A month that was just published is stamped once, on the next Home view. */
const STAMP_KEY = 'fc.sealed';
export function rememberSealed(key: string) {
  try { sessionStorage.setItem(STAMP_KEY, key); } catch { /* storage unavailable: no animation */ }
}
export function takeSealed(): string | null {
  try {
    const v = sessionStorage.getItem(STAMP_KEY);
    if (v) sessionStorage.removeItem(STAMP_KEY);
    return v;
  } catch { return null; }
}

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Count a figure up once when it first appears. Respects reduced motion. */
export function useCountUp(target: number | null, ms = 700): number | null {
  const [v, setV] = useState<number | null>(target === null || reducedMotion() ? target : 0);
  useEffect(() => {
    if (target === null || reducedMotion()) { setV(target); return; }
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

/** True on the open Fold and wider: two panes instead of one column. */
export function useWide(minWidth = 840): boolean {
  const q = `(min-width: ${minWidth}px)`;
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [q]);
  return wide;
}
